import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, MapPin, Send, Wifi } from 'lucide-react';
import { Button } from './Button';
const INTERESTS = ['Check fiber availability', 'Compare fiber plan options', 'Talk to a fiber specialist', 'General fiber internet question'];
export function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    interest: INTERESTS[0],
    message: ''
  });
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
  };
  const inputClass = 'w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/30';
  return <section id="contact" className="w-full bg-secondary py-20 lg:py-28" data-id="element-27">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8" data-id="element-28">
        <div data-id="element-29">
          <p className="text-sm font-semibold uppercase tracking-wide text-brand-dark" data-id="element-30">
            Get Started
          </p>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl" data-id="element-31">
            Check fiber availability for your home
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-muted-foreground" data-id="element-32">
            Share a few details and a member of the AllStar team will follow up
            to discuss fiber internet availability and next steps for your
            Vancouver-area home.
          </p>

          <div className="mt-10 space-y-6" data-id="element-33">
            <div className="flex items-start gap-4" data-id="element-34">
              <span className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand-dark" data-id="element-35">
                <MapPin className="h-5 w-5" aria-hidden="true" data-id="element-36" />
              </span>
              <div data-id="element-37">
                <p className="font-semibold text-foreground" data-id="element-38">
                  Serving Vancouver, Washington
                </p>
                <p className="text-muted-foreground" data-id="element-39">
                  Start with your home address to explore local fiber
                  availability.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-id="element-40">
              <span className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand-dark" data-id="element-41">
                <Wifi className="h-5 w-5" aria-hidden="true" data-id="element-42" />
              </span>
              <div data-id="element-43">
                <p className="font-semibold text-foreground" data-id="element-44">
                  Focused on fiber internet
                </p>
                <p className="text-muted-foreground" data-id="element-45">
                  Get help understanding the options for your household
                  connection.
                </p>
              </div>
            </div>
          </div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }} className="rounded-2xl bg-background p-6 shadow-sm sm:p-8" data-id="element-46">
          {submitted ? <div className="flex min-h-72 flex-col items-center justify-center text-center" data-id="element-47">
              <CheckCircle2 className="h-14 w-14 text-brand" aria-hidden="true" data-id="element-48" />
              <h3 className="mt-4 font-heading text-2xl font-semibold text-foreground" data-id="element-49">
                Thanks, {form.name || 'there'}!
              </h3>
              <p className="mt-2 max-w-sm text-muted-foreground" data-id="element-50">
                Your request has been received. The AllStar team will be in
                touch about your fiber internet inquiry.
              </p>
            </div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-51">
              <div data-id="element-52">
                <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground" data-id="element-53">
                  Full name
                </label>
                <input id="name" type="text" required value={form.name} onChange={event => setForm({
              ...form,
              name: event.target.value
            })} className={inputClass} placeholder="Jane Doe" data-id="element-54" />
              </div>
              <div data-id="element-55">
                <label htmlFor="phone" className="mb-1.5 block text-sm font-medium text-foreground" data-id="element-56">
                  Phone number
                </label>
                <input id="phone" type="tel" required value={form.phone} onChange={event => setForm({
              ...form,
              phone: event.target.value
            })} className={inputClass} placeholder="(555) 555-5555" data-id="element-57" />
              </div>
              <div data-id="element-58">
                <label htmlFor="interest" className="mb-1.5 block text-sm font-medium text-foreground" data-id="element-59">
                  How can we help?
                </label>
                <select id="interest" value={form.interest} onChange={event => setForm({
              ...form,
              interest: event.target.value
            })} className={inputClass} data-id="element-60">
                  {INTERESTS.map(option => <option key={option} value={option} data-id="element-61">
                      {option}
                    </option>)}
                </select>
              </div>
              <div data-id="element-62">
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground" data-id="element-63">
                  Home address or message{' '}
                  <span className="text-muted-foreground" data-id="element-64">(optional)</span>
                </label>
                <textarea id="message" rows={3} value={form.message} onChange={event => setForm({
              ...form,
              message: event.target.value
            })} className={inputClass} placeholder="Share your address or tell us what you need…" data-id="element-65" />
              </div>
              <Button type="submit" size="lg" className="h-12 w-full bg-brand text-base text-brand-foreground hover:bg-brand-dark" data-id="element-66">
                Request an availability check{' '}
                <Send className="h-4 w-4" aria-hidden="true" data-id="element-67" />
              </Button>
            </form>}
        </motion.div>
      </div>
    </section>;
}